import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '../api/http.js';
import { useAdminAuth } from '../auth/AdminAuthContext.jsx';
import { useUI } from '../ui/UIProvider.jsx';
import { KpiTile } from '../components/KpiTile.jsx';
import { CountryFlag, countryName } from '../components/CountryFlag.jsx';
import { Icon } from '../components/icons.jsx';

/**
 * Finances dashboard. Reads three endpoints on mount:
 *
 *   GET /api/admin/finances/overview     — MRR, ARR, churn, paying count
 *   GET /api/admin/finances/by-country   — { country, users, paidUsers, mrrPence }[]
 *   GET /api/admin/finances/transactions — { id, amountPence, status, method, at, user }[]
 *
 * Numbers are approximate (see AdminFinancesController#overview) —
 * computed from the user table, not from a Stripe webhook ledger.
 * The "approx" note from the server's payload is surfaced verbatim
 * at the bottom so an operator knows when to fall back to the Stripe
 * Dashboard for ground truth.
 *
 * Refund flow:
 *   1. Click the refund chip on a transaction row → modal opens
 *      pre-filled with the charge id + amount
 *   2. Submit → POST /api/admin/finances/refund with { chargeId,
 *      amountPence, reason }
 *   3. Server returns 501 by design — it logs the INTENT in the audit
 *      table and expects the admin to issue the actual refund in the
 *      Stripe dashboard. We surface that as a green "intent captured"
 *      toast, not an error, so the operator knows the next step.
 *
 * Role gating:
 *   - SUPPORT_AGENT: cap input at £50
 *   - SUPPORT_LEAD:  cap input at £200
 *   - SUPERADMIN:    no client-side cap (server still validates)
 *
 * @author <lukman@zshift.io>
 */

// Role → refund ceiling in pounds. Mirrors AdminRole.refundCeilingPence
// (which is misnamed — server constant is actually in £, multiplied by
// 100 in the controller to convert to pence). Keeping the lookup local
// so the page can decide whether to render the refund chip and what
// max= attribute to put on the amount input.
//
// The legacy READ_ONLY role is gone — the AdminRoleMigrator on the
// server rewrites any leftover rows to SUPPORT_AGENT on startup, so
// every authenticated admin now lands on one of the three keys here.
const REFUND_CEILING_GBP = {
  SUPERADMIN:    Number.MAX_SAFE_INTEGER,
  SUPPORT_LEAD:  200,
  SUPPORT_AGENT: 50,
};

export function Finances() {
  const { me } = useAdminAuth();
  const { toast } = useUI();

  const [loading, setLoading]     = useState(true);
  const [overview, setOverview]   = useState(null);
  const [countries, setCountries] = useState([]);
  const [txs, setTxs]             = useState([]);

  // Refund modal state. `null` when closed; otherwise the transaction
  // row being refunded.
  const [refundTarget, setRefundTarget] = useState(null);

  // Country-table search. Free text — matched case-insensitively
  // against the ISO code AND the localised country name so an
  // operator can type "United" or "GB" and land on the same row.
  const [countryQuery, setCountryQuery] = useState('');

  const refundCeilingGbp = REFUND_CEILING_GBP[me?.role] ?? 0;
  const canRefund = refundCeilingGbp > 0;

  // ── Initial fetch ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const safe = (p, fallback) => p.catch((e) => { console.warn(e); return fallback; });

    Promise.all([
      safe(api('/api/admin/finances/overview'),     null),
      safe(api('/api/admin/finances/by-country'),   []),
      safe(api('/api/admin/finances/transactions?limit=50'), []),
    ]).then(([o, c, t]) => {
      if (cancelled) return;
      setOverview(o);
      setCountries(Array.isArray(c) ? c : []);
      setTxs(Array.isArray(t) ? t : []);
      setLoading(false);
    });

    return () => { cancelled = true; };
  }, []);

  // ── Derived numbers ───────────────────────────────────────────
  const mrrPence       = overview?.mrrPence       || 0;
  const arrPence       = overview?.arrPence       || (mrrPence * 12);
  const churnPct       = overview?.monthlyChurnPct ?? 0;
  const paidUsers      = overview?.paidUsers      || 0;
  const premiumRows    = overview?.premiumRows    || 0;
  const compedOrLapsed = overview?.compedOrLapsed || 0;

  // Country bar scales by max MRR so the leader is full-width.
  // We deliberately use the UNFILTERED leader so the bar widths stay
  // comparable between "all countries" and a filtered subset — without
  // this, typing a search query would rescale every bar to 100%, which
  // would visually obscure how small the filtered country actually is
  // relative to the full market.
  const topMrr = countries[0]?.mrrPence || 1;

  // Apply the country-table search. ISO code match short-circuits;
  // name match falls back to Intl.DisplayNames (US English by default
  // since `countryName` reads navigator.language). Empty query →
  // pass-through, no allocation.
  const filteredCountries = useMemo(() => {
    const q = countryQuery.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter((c) => {
      const iso = String(c.country || '').toLowerCase();
      if (iso.includes(q)) return true;
      const name = (countryName(c.country) || '').toLowerCase();
      return name.includes(q);
    });
  }, [countries, countryQuery]);

  // ── Refund handler ────────────────────────────────────────────
  const submitRefund = useCallback(async ({ chargeId, amountPence, reason }) => {
    try {
      await api('/api/admin/finances/refund', {
        method: 'POST',
        body: { chargeId, amountPence, reason },
      });
      // Successful 2xx isn't expected — the endpoint is wired to throw
      // 501 NOT_IMPLEMENTED by design. If we somehow land here, treat
      // it as a normal success path.
      toast.success('Refund processed.');
      setRefundTarget(null);
    } catch (e) {
      // 501 is the "intent captured" path — surface it as a friendly
      // success-style message with follow-up instructions, not as an
      // error toast. Any other status (403 = ceiling exceeded, 400 =
      // bad input) bubbles up as a real error.
      const status = e?.status;
      const isIntentCaptured = status === 501;
      if (isIntentCaptured) {
        toast.success('Refund intent logged. Issue it in the Stripe dashboard.');
        setRefundTarget(null);
      } else {
        toast.error(e?.message || 'Refund failed.');
      }
    }
  }, [toast]);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Finances</h1>
          <p className="page-sub">
            Revenue, refunds, and payouts. Numbers computed from the user table —
            approximate; see <em>Stripe Dashboard → Reports</em> for proration-accurate figures.
          </p>
        </div>
      </div>

      {/* ── KPI row ─────────────────────────────────────────────── */}
      <div className="kpi-row">
        <KpiTile
          label="MRR"
          iconName="currency"
          iconColor="green"
          value={mrrPence / 100}
          valueFormatter={(v) => poundsFmt(v)}
          delta={{ raw: `${paidUsers.toLocaleString()} paying`, post: '· £3.99 each' }}
          loading={loading}
        />
        <KpiTile
          label="ARR (run-rate)"
          iconName="finances"
          iconColor="blue"
          value={arrPence / 100}
          valueFormatter={(v) => poundsFmt(v)}
          delta={{ post: 'MRR × 12 — no annual proration' }}
          loading={loading}
        />
        <KpiTile
          label="Monthly churn"
          iconName="arrowDown"
          iconColor="orange"
          value={churnPct}
          valueFormatter={(v) => `${v.toFixed(1)}%`}
          delta={{ post: 'rolling 30-day window' }}
          loading={loading}
        />
        <KpiTile
          label="Premium accounts"
          iconName="shield"
          value={premiumRows}
          valueFormatter={(v) => v.toLocaleString()}
          delta={{
            // Show the comped/lapsed gap so the gap between "rows"
            // and "paying" is visible at a glance — an admin can
            // tell whether the discrepancy is from free passes or
            // from billing failures that haven't been reconciled.
            post: compedOrLapsed > 0
              ? `${compedOrLapsed.toLocaleString()} comped or lapsed`
              : 'all currently paying',
          }}
          loading={loading}
        />
      </div>

      {/* ── Visual breakdowns: country donut + payment-method donut ── */}
      <div className="row r-1-1">
        <MrrByCountryDonut countries={countries} loading={loading} />
        <MrrByMethodDonut transactions={txs} loading={loading} />
      </div>

      {/* ── Revenue by country ──────────────────────────────────── */}
      <div className="row">
        <div className="card">
          <div className="card-head">
            <h3>Revenue by country</h3>
            {/* Sub-text doubles as the result counter. When a query is
                active it reads "X of Y" so the operator sees how much
                they're filtering out — useful when typing a partial
                name and only one of several matches is intended. */}
            <div className="sub">
              {countryQuery.trim()
                ? `${filteredCountries.length.toLocaleString()} of ${countries.length.toLocaleString()} markets`
                : `${countries.length.toLocaleString()} markets · MRR sorted`}
            </div>
          </div>

          {/* Search bar lives in its own strip above the table so the
              sticky-header pattern below doesn't have to scroll with
              it. Matches against ISO code OR localised name (see
              filteredCountries memo). */}
          <div style={{
            padding: '10px 16px',
            borderBottom: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <Icon name="search" size={14} style={{ color: 'var(--ink-3)' }} />
            <input
              type="search"
              value={countryQuery}
              onChange={(e) => setCountryQuery(e.target.value)}
              placeholder="Filter by country code or name — e.g. GB, United States…"
              aria-label="Filter countries"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                font: 'inherit',
                fontSize: 13,
                color: 'var(--ink)',
                padding: '4px 0',
              }}
            />
            {countryQuery && (
              <button
                type="button"
                className="btn ghost sm"
                onClick={() => setCountryQuery('')}
                aria-label="Clear filter"
                style={{ padding: '4px 8px' }}
              >
                Clear
              </button>
            )}
          </div>

          {/* Scrollable table body. Capped at ~520px so the whole
              Finances page stays usable on a single laptop screen —
              the operator scrolls the country list vertically inside
              the card instead of growing the page to several screens.
              Header stays put via position: sticky on the <th>s. */}
          <div className="table-wrap" style={{ maxHeight: 520, overflowY: 'auto' }}>
            <table className="t">
              <thead>
                <tr>
                  <th style={{ width: 40, position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1 }}>#</th>
                  <th style={{ position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1 }}>Country</th>
                  <th style={{ textAlign: 'right', position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1 }}>Users</th>
                  <th style={{ textAlign: 'right', position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1 }}>Paying</th>
                  <th style={{ textAlign: 'right', position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1 }}>MRR</th>
                  <th style={{ width: '32%', position: 'sticky', top: 0, background: 'var(--card)', zIndex: 1 }}>Share</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</td></tr>
                )}
                {!loading && countries.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
                    No country data yet.
                  </td></tr>
                )}
                {!loading && countries.length > 0 && filteredCountries.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
                    No countries match <strong>“{countryQuery}”</strong>.
                  </td></tr>
                )}
                {!loading && filteredCountries.map((c, i) => {
                  // Rank reflects the global sort position (MRR
                  // descending across all countries), not the filtered
                  // subset — so a filter for "Brazil" still tells you
                  // BR is the #7 market overall, not "#1 of 1".
                  const globalRank = countries.indexOf(c) + 1;
                  return (
                    <tr key={c.country}>
                      <td className="mono" style={{ color: 'var(--ink-3)' }}>{globalRank}</td>
                      <td>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          <CountryFlag code={c.country} width={18} />
                          <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 12 }}>
                            {c.country || '??'}
                          </span>
                          {/* Render the localised name next to the
                              code so the search query is justified
                              ("oh, that IS the United Kingdom"). */}
                          {countryName(c.country) && (
                            <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                              {countryName(c.country)}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'right' }}>{Number(c.users || 0).toLocaleString()}</td>
                      <td style={{ textAlign: 'right' }}>{Number(c.paidUsers || 0).toLocaleString()}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>
                        {poundsFmt((c.mrrPence || 0) / 100)}
                      </td>
                      <td>
                        <div className="map-bar-wrap" aria-hidden="true">
                          <div
                            className="map-bar"
                            style={{
                              width: `${Math.max(2, ((c.mrrPence || 0) / topMrr) * 100)}%`,
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Recent transactions ──────────────────────────────────── */}
      <div className="row">
        <div className="card">
          <div className="card-head">
            <h3>Recent transactions</h3>
            <div className="sub">
              {canRefund
                ? `Refunds capped at £${refundCeilingGbp.toLocaleString()} for your role`
                : 'Read-only — refunds disabled for your role'}
            </div>
          </div>
          <div className="table-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Method</th>
                  <th style={{ textAlign: 'right' }}>Amount</th>
                  <th>Status</th>
                  <th>When</th>
                  <th style={{ width: 100, textAlign: 'right' }}></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>Loading…</td></tr>
                )}
                {!loading && txs.length === 0 && (
                  <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
                    No recent transactions.
                  </td></tr>
                )}
                {!loading && txs.map((tx) => (
                  <tr key={tx.id}>
                    <td>
                      <div className="user-cell">
                        <div className={`av v${((tx.user?.id || 0) % 10) + 1}`}>
                          {initials(tx.user?.name || tx.user?.email)}
                        </div>
                        <div>
                          <strong>{tx.user?.name || '—'}</strong>
                          <em>{tx.user?.email || '—'}</em>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="mono" style={{ fontSize: 12, color: 'var(--ink-2)' }}>
                        {prettyMethod(tx.method)}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>
                      {poundsFmt((tx.amountPence || 0) / 100)}
                    </td>
                    <td>
                      <span className={`pill ${tx.status === 'ok' ? 'success' : 'warn'}`}>
                        <span className="dot"/> {tx.status || '—'}
                      </span>
                    </td>
                    <td className="mono" style={{ color: 'var(--ink-3)', fontSize: 12 }}>
                      {tx.at ? agoString(tx.at) : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {canRefund && (
                        <button
                          className="btn ghost sm"
                          onClick={() => setRefundTarget(tx)}
                          title="Refund this charge"
                        >
                          Refund
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Approx note ──────────────────────────────────────────── */}
      {!loading && overview?.approxNote && (
        <div style={{
          marginTop: 22,
          padding: 14,
          background: 'var(--bg-2)',
          borderRadius: 10,
          fontSize: 12,
          color: 'var(--ink-3)',
          lineHeight: 1.5,
        }}>
          <strong style={{ color: 'var(--ink-2)' }}>About these numbers · </strong>
          {overview.approxNote}
        </div>
      )}

      {/* ── Refund modal ─────────────────────────────────────────── */}
      {refundTarget && (
        <RefundModal
          tx={refundTarget}
          ceilingGbp={refundCeilingGbp}
          onClose={() => setRefundTarget(null)}
          onSubmit={submitRefund}
        />
      )}
    </div>
  );
}

// ── Refund modal ──────────────────────────────────────────────────
function RefundModal({ tx, ceilingGbp, onClose, onSubmit }) {
  // Pre-fill the amount with the full charge amount in £ (with
  // decimals). The user can lower it for a partial refund. Capped
  // by the role's ceiling — input has max= AND we re-clamp on
  // submit to belt-and-brace.
  const fullPounds = ((tx.amountPence || 0) / 100).toFixed(2);
  const [amountStr, setAmountStr] = useState(fullPounds);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const amountPounds = Number(amountStr) || 0;
  const amountPence  = Math.round(amountPounds * 100);

  const ceilingDisplay = Number.isFinite(ceilingGbp) && ceilingGbp < 1_000_000
    ? `£${ceilingGbp.toLocaleString()}`
    : 'no cap';

  const overCap = ceilingGbp < 1_000_000 && amountPounds > ceilingGbp;
  const overCharge = amountPence > (tx.amountPence || 0);
  const invalid = busy || amountPence <= 0 || overCap || overCharge;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (invalid) return;
    setBusy(true);
    try {
      await onSubmit({
        chargeId: tx.id,
        amountPence,
        reason: reason.trim(),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="modal-head">
          <h3>Refund charge</h3>
          <button type="button" className="btn ghost sm" onClick={onClose} aria-label="Close">
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
              Charge
            </div>
            <div className="mono" style={{ fontSize: 13 }}>{tx.id}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
              {tx.user?.email || '—'} · originally {poundsFmt((tx.amountPence || 0) / 100)}
            </div>
          </div>

          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-2)', marginBottom: 4 }}>
              Amount (£)
            </span>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={Math.min(ceilingGbp, (tx.amountPence || 0) / 100)}
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '9px 11px',
                border: '1px solid var(--line)',
                borderRadius: 8,
                fontSize: 14,
                fontFamily: 'Geist Mono, monospace',
              }}
            />
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>
              Your role's refund ceiling: {ceilingDisplay}
            </div>
          </label>

          <label style={{ display: 'block', marginBottom: 12 }}>
            <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-2)', marginBottom: 4 }}>
              Reason (audit log)
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="e.g. duplicate charge, customer request, billing error…"
              style={{
                width: '100%',
                padding: '9px 11px',
                border: '1px solid var(--line)',
                borderRadius: 8,
                fontSize: 13,
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </label>

          {/* Inline validation — beats a popup dialog for fast, in-form fixes. */}
          {overCap && (
            <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 8 }}>
              Amount exceeds your role's ceiling of {ceilingDisplay}.
            </div>
          )}
          {overCharge && (
            <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 8 }}>
              Amount exceeds the original charge.
            </div>
          )}

          <div style={{
            padding: 10,
            background: 'var(--bg-2)',
            borderRadius: 8,
            fontSize: 11,
            color: 'var(--ink-3)',
            lineHeight: 1.5,
          }}>
            <strong style={{ color: 'var(--ink-2)' }}>Heads-up · </strong>
            This logs the refund <em>intent</em> in the audit table. Issue the
            actual refund in the Stripe dashboard — a future sync job will stamp
            this row with the Stripe refund id.
          </div>
        </div>
        <div className="modal-foot">
          <button type="button" className="btn ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn primary" disabled={invalid}>
            {busy ? 'Logging…' : 'Log refund intent'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── MRR by country donut ─────────────────────────────────────────
//
// Top 5 markets get their own arc; everything else collapses into an
// "Other" slice so the legend stays readable. Picked donut over bar
// chart for this one because the country *table* below already shows
// per-row bars — the donut gives the at-a-glance concentration story
// (e.g. "60% of MRR comes from one country") in a way a long table
// doesn't.
//
// Arc math mirrors Overview.jsx's PlanDonutCard:
//   radius 15.915 → 2πr ≈ 100, so each dasharray value reads as a %
//   dashoffset = 25 - (sum of preceding arcs) → starts at top
const COUNTRY_PALETTE = [
  '#6B3FD4', // brand purple
  '#FF6B47', // brand orange
  '#2D43E0', // brand blue
  '#10A37F', // brand green
  '#F0B86E', // soft amber
  '#C7BCD8', // muted grey-purple — reserved for "Other"
];

function MrrByCountryDonut({ countries, loading }) {
  const { slices, total } = useMemo(() => {
    const filtered = (countries || []).filter((c) => (c.mrrPence || 0) > 0);
    if (filtered.length === 0) return { slices: [], total: 0 };

    const top = filtered.slice(0, 5);
    const rest = filtered.slice(5);
    const restMrr = rest.reduce((s, c) => s + (c.mrrPence || 0), 0);
    const arr = top.map((c, i) => ({
      label: c.country || '??',
      mrr:   c.mrrPence || 0,
      color: COUNTRY_PALETTE[i],
      flag:  c.country || null,
    }));
    if (restMrr > 0) {
      arr.push({
        label: `Other (${rest.length})`,
        mrr:   restMrr,
        color: COUNTRY_PALETTE[5],
        flag:  null,
      });
    }
    const tot = arr.reduce((s, x) => s + x.mrr, 0);
    return { slices: arr, total: tot };
  }, [countries]);

  // Pre-compute dashoffsets for each arc — running sum of preceding
  // percentages, subtracted from 25 (the "start at top" offset).
  const arcs = useMemo(() => {
    let acc = 0;
    return slices.map((s) => {
      const pct = total > 0 ? (100 * s.mrr) / total : 0;
      const offset = 25 - acc;
      acc += pct;
      return { ...s, pct, offset };
    });
  }, [slices, total]);

  return (
    <div className="card donut-card">
      <div className="card-head" style={{ alignSelf: 'stretch', borderBottom: 'none', paddingBottom: 0 }}>
        <h3>MRR by country</h3>
        <div className="sub">TOP 5 + OTHER</div>
      </div>
      <div className="donut-wrap">
        <svg viewBox="0 0 42 42" width="200" height="200" role="img"
             aria-label="Monthly recurring revenue split by country">
          <circle cx="21" cy="21" r="15.915" fill="none" stroke="#ECE3F7" strokeWidth="6"/>
          {!loading && total > 0 && arcs.map((arc) => (
            <circle
              key={arc.label}
              cx="21" cy="21" r="15.915"
              fill="none"
              stroke={arc.color}
              strokeWidth="6"
              strokeDasharray={`${arc.pct.toFixed(3)} ${(100 - arc.pct).toFixed(3)}`}
              strokeDashoffset={arc.offset.toFixed(3)}
              transform="rotate(-90 21 21)"
            >
              <title>{`${arc.label} — ${poundsFmt(arc.mrr / 100)} · ${arc.pct.toFixed(1)}%`}</title>
            </circle>
          ))}
        </svg>
        <div className="donut-center">
          <span className="v">{loading ? '—' : poundsFmt(total / 100)}</span>
          <span className="lbl">Total MRR</span>
        </div>
      </div>
      <div className="donut-legend">
        {loading && (
          <div style={{ padding: 8, textAlign: 'center', fontSize: 12, color: 'var(--ink-3)' }}>
            Loading…
          </div>
        )}
        {!loading && arcs.length === 0 && (
          <div style={{ padding: 8, textAlign: 'center', fontSize: 12, color: 'var(--ink-3)' }}>
            No paid users in any country yet.
          </div>
        )}
        {!loading && arcs.map((arc) => (
          <div className="donut-leg-row" key={arc.label}>
            <span className="sw" style={{ background: arc.color }} />
            <span className="nm" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              {arc.flag && <CountryFlag code={arc.flag} width={14} />}
              {arc.label}
            </span>
            <span className="mono" style={{ color: 'var(--ink-3)', fontSize: 11, marginRight: 8 }}>
              {arc.pct.toFixed(1)}%
            </span>
            <span className="nb">{poundsFmt(arc.mrr / 100)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── MRR by payment method donut ──────────────────────────────────
//
// Buckets the recent-transactions list by method (Stripe / Apple via
// RevenueCat / Google via RevenueCat / unknown). Surfaces platform
// concentration — useful to spot the day Stripe goes down (no Stripe
// rows in 24h) or the week iOS conversions spike.
//
// The transactions endpoint currently returns up to 50 rows and uses
// User.updatedAt as a proxy timestamp; counts here aren't true MRR
// (we'd need a per-user subscription_source roll-up from the server
// for that), but the SHAPE of the split is accurate enough for the
// dashboard's "where does the money flow" glance.
// RC_COMBINED catches today's server output ("revenuecat" with no
// platform suffix). RC_IOS / RC_ANDROID are pre-wired for a future
// server change that splits the store; until then RC_COMBINED carries
// 100% of mobile store revenue and the two split buckets stay empty
// (and therefore don't render — see the !skip-zero loop below).
const METHOD_META = {
  STRIPE:        { label: 'Stripe',                       color: '#635BFF' }, // Stripe brand purple
  RC_COMBINED:   { label: 'RevenueCat (App Store / Play)', color: '#0A84FF' }, // iOS blue — RC's primary surface
  RC_IOS:        { label: 'App Store · RC',               color: '#0A84FF' }, // iOS blue
  RC_ANDROID:    { label: 'Play · RC',                    color: '#10A37F' }, // Android green
  OTHER:         { label: 'Other / Unknown',              color: '#C7BCD8' },
};

function MrrByMethodDonut({ transactions, loading }) {
  const { arcs, total, sampleCount } = useMemo(() => {
    const buckets = { STRIPE: 0, RC_COMBINED: 0, RC_IOS: 0, RC_ANDROID: 0, OTHER: 0 };
    let n = 0;
    for (const tx of (transactions || [])) {
      const pence = tx.amountPence || 0;
      if (pence <= 0) continue;
      n++;
      const m = String(tx.method || '').toLowerCase();
      if (m === 'stripe') buckets.STRIPE += pence;
      // Bare "revenuecat" — what the server currently writes for both
      // iOS and Android mobile IAP. Group as one slice so the donut
      // doesn't lump store revenue into "Other".
      else if (m === 'revenuecat') buckets.RC_COMBINED += pence;
      // Forward-compat: if the server later starts emitting
      // "rc_ios" / "rc_android", split them into their own slices
      // automatically.
      else if (m === 'rc_ios'     || m === 'revenuecat_ios')     buckets.RC_IOS     += pence;
      else if (m === 'rc_android' || m === 'revenuecat_android') buckets.RC_ANDROID += pence;
      else buckets.OTHER += pence;
    }
    const tot = Object.values(buckets).reduce((s, v) => s + v, 0);

    // Build arcs only for buckets that have value; otherwise we'd render
    // invisible 0-length arcs (and add the bucket to the legend, which
    // looks broken with "0%").
    let acc = 0;
    const list = [];
    for (const key of ['STRIPE', 'RC_COMBINED', 'RC_IOS', 'RC_ANDROID', 'OTHER']) {
      const v = buckets[key];
      if (v <= 0) continue;
      const pct = tot > 0 ? (100 * v) / tot : 0;
      list.push({
        key,
        ...METHOD_META[key],
        mrr: v,
        pct,
        offset: 25 - acc,
      });
      acc += pct;
    }
    return { arcs: list, total: tot, sampleCount: n };
  }, [transactions]);

  return (
    <div className="card donut-card">
      <div className="card-head" style={{ alignSelf: 'stretch', borderBottom: 'none', paddingBottom: 0 }}>
        <h3>Payment platform</h3>
        <div className="sub">
          {loading ? '—' : `BASED ON ${sampleCount.toLocaleString()} RECENT TX`}
        </div>
      </div>
      <div className="donut-wrap">
        <svg viewBox="0 0 42 42" width="200" height="200" role="img"
             aria-label="Recent revenue split by payment platform">
          <circle cx="21" cy="21" r="15.915" fill="none" stroke="#ECE3F7" strokeWidth="6"/>
          {!loading && total > 0 && arcs.map((arc) => (
            <circle
              key={arc.key}
              cx="21" cy="21" r="15.915"
              fill="none"
              stroke={arc.color}
              strokeWidth="6"
              strokeDasharray={`${arc.pct.toFixed(3)} ${(100 - arc.pct).toFixed(3)}`}
              strokeDashoffset={arc.offset.toFixed(3)}
              transform="rotate(-90 21 21)"
            >
              <title>{`${arc.label} — ${poundsFmt(arc.mrr / 100)} · ${arc.pct.toFixed(1)}%`}</title>
            </circle>
          ))}
        </svg>
        <div className="donut-center">
          <span className="v">{loading ? '—' : poundsFmt(total / 100)}</span>
          <span className="lbl">Sampled</span>
        </div>
      </div>
      <div className="donut-legend">
        {loading && (
          <div style={{ padding: 8, textAlign: 'center', fontSize: 12, color: 'var(--ink-3)' }}>
            Loading…
          </div>
        )}
        {!loading && arcs.length === 0 && (
          <div style={{ padding: 8, textAlign: 'center', fontSize: 12, color: 'var(--ink-3)' }}>
            No transactions to sample yet.
          </div>
        )}
        {!loading && arcs.map((arc) => (
          <div className="donut-leg-row" key={arc.key}>
            <span className="sw" style={{ background: arc.color }} />
            <span className="nm">{arc.label}</span>
            <span className="mono" style={{ color: 'var(--ink-3)', fontSize: 11, marginRight: 8 }}>
              {arc.pct.toFixed(1)}%
            </span>
            <span className="nb">{poundsFmt(arc.mrr / 100)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Local helpers ────────────────────────────────────────────────
function poundsFmt(v) {
  // Always two decimal places — accounting convention. £1 → £1.00, not £1.
  return '£' + Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function initials(name) {
  if (!name) return '—';
  return name.split(/\s+/).filter(Boolean).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}

function agoString(iso) {
  const ts = new Date(iso).getTime();
  if (!ts) return '—';
  const mins = Math.max(0, Math.floor((Date.now() - ts) / 60000));
  if (mins < 1)  return 'now';
  if (mins < 60) return mins + 'm ago';
  const h = Math.floor(mins / 60);
  if (h < 24)    return h + 'h ago';
  const d = Math.floor(h / 24);
  if (d < 30)    return d + 'd ago';
  return Math.floor(d / 30) + 'mo ago';
}

// Pretty up the payment-method string the server stuffs in
// User.subscriptionSource. Real values currently observed:
//   - "stripe"      — web checkout (BillingService.applySubscriptionUpdate)
//   - "revenuecat"  — mobile IAP (BillingService.applyRevenuecatEntitlements)
//                     Note: the server doesn't split iOS vs Android — RC's
//                     webhook payload carries the store but we don't
//                     persist that yet. Both platforms collapse here.
// Future split keys (`rc_ios`, `rc_android`) are pre-handled so a server
// change won't require a client patch.
function prettyMethod(s) {
  if (!s) return '—';
  const m = String(s).toLowerCase();
  if (m === 'stripe')                                   return 'Stripe';
  if (m === 'revenuecat')                               return 'RevenueCat (App Store / Play)';
  if (m === 'rc_ios' || m === 'revenuecat_ios')         return 'App Store · RC';
  if (m === 'rc_android' || m === 'revenuecat_android') return 'Play · RC';
  if (m.startsWith('rc_'))                              return 'RevenueCat';
  return s;
}
